import { types } from "mobx-state-tree";
import type { DocumentRowMapped } from "./repository";

/**
 * Design discipline model (from design_disciplines table via junction).
 */
export const DesignDisciplineModel = types.model("DesignDisciplineModel", {
  id: types.number,
  name: types.string,
  name_en: types.maybeNull(types.string),
  abbreviation: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
});

/**
 * Design stage model (from design_stages table via junction).
 */
export const DesignStageModel = types.model("DesignStageModel", {
  id: types.number,
  name: types.string,
  name_en: types.maybeNull(types.string),
  abbreviation: types.maybeNull(types.string),
});

/**
 * Document category model (from document_categories table via junction).
 */
export const DocumentCategoryModel = types.model("DocumentCategoryModel", {
  id: types.identifierNumber,
  name: types.string,
  name_et: types.maybeNull(types.string),
});

/**
 * WHA-3270 — PM issue reference attached to a document via
 * `pm_issue_documents`. Rendered by the `LinkedIssuesChip` next to the
 * preview icon on document rows.
 */
export const LinkedIssueRefModel = types.model("LinkedIssueRefModel", {
  id: types.number,
  issue_number: types.number,
  title: types.string,
  building_id: types.number,
  // types.string, not an enumeration: a project can define its own statuses,
  // and an unknown member is a RUNTIME failure here, not a type error -- the
  // whole linked-issue chip would fail to instantiate.
  status: types.string,
});

/**
 * Minimal document model for list views.
 * Contains only essential fields needed for table display.
 */
export const DocumentModel = types
  .model("DocumentModel", {
    id: types.identifier, // String ID for UI consistency
    filename: types.string,
    file_path: types.string,
    building_id: types.string,
    parent_document_id: types.maybeNull(types.string),
    content_type: types.string,
    size: types.number,
    created_at: types.string,
    original_created_at: types.maybeNull(types.string),
    modified_at: types.maybeNull(types.string),
    document_content_modified_date: types.maybeNull(types.string),
    // Generated COALESCE of the three date columns — see getDocTimestamp.
    effective_modified_at: types.maybeNull(types.string),
    created_by: types.number,
    created_by_name: types.maybeNull(types.string),
    created_by_avatar_url: types.maybeNull(types.string),
    created_by_color: types.maybeNull(types.string),
    state: types.maybeNull(types.string),
    version: types.maybeNull(types.number),
    version_group: types.maybeNull(types.string),
    // Who filed it (`documents.source`); "brief_appendices" marks Franklin's PDF.
    source: types.maybeNull(types.string),
    // Container routing tag ("dwg" | "ifc" | "zip"); preserved so a new-version
    // re-upload can keep the container's pipeline routing.
    container_type: types.maybeNull(types.string),
    project_id: types.maybeNull(types.number),
    folder_id: types.maybeNull(types.string),
    is_deleted: types.optional(types.boolean, false),
    approved: types.optional(types.boolean, false),
    // Verified mark. `verified_at !== null` IS the boolean — there is no
    // separate flag column. `verified_by_name` is resolved through the
    // documents_verified_by_fk embed for the hover attribution.
    verified_at: types.maybeNull(types.string),
    verified_by: types.maybeNull(types.number),
    verified_by_name: types.maybeNull(types.string),
    // Tag-related fields from junction tables
    categories: types.optional(types.array(DocumentCategoryModel), []),
    design_disciplines: types.optional(types.array(DesignDisciplineModel), []),
    design_stages: types.optional(types.array(DesignStageModel), []),
    // WHA-3270 — PM issues this document is attached to (rendered as a
    // chip next to the preview icon in the documents/timeline/project
    // tables).
    linked_issues: types.optional(types.array(LinkedIssueRefModel), []),
  })
  .actions((self) => ({
    /** Patch the in-memory filename so observer-based table rows re-render
     *  immediately after a successful rename, without waiting for the
     *  refetch round-trip. (WHA-3259) */
    setFilename(newFilename: string) {
      self.filename = newFilename;
    },
    /** Patch the in-memory Verified mark so observer-based rows re-render
     *  immediately after a verify/unverify, without waiting for the refetch.
     *  Values come from the server's own response — the DB stamps them, so the
     *  client never invents a timestamp or a verifier. */
    setVerified(at: string | null, by: number | null, byName: string | null) {
      self.verified_at = at;
      self.verified_by = by;
      self.verified_by_name = byName;
    },
  }));

/**
 * Document selection model for multi-select state.
 */
export const DocumentSelectionModel = types.model("DocumentSelectionModel", {
  selectedIds: types.map(types.boolean), // Map<documentId, isSelected>
});

/**
 * Building project model for tags.
 */
export const ProjectTagModel = types.model("ProjectTagModel", {
  id: types.identifier, // String ID
  building_id: types.string,
  name: types.string,
  description: types.maybeNull(types.string),
  created_at: types.string,
  created_by: types.string,
  modified_at: types.string,
  modified_by: types.maybeNull(types.string),
  org_id: types.string,
  is_default: types.boolean,
});

/**
 * Document folder model for tags.
 */
export const FolderTagModel = types.model("FolderTagModel", {
  id: types.identifier, // String ID
  name: types.string,
  parentId: types.maybeNull(types.string),
  category: types.enumeration(["documents", "drawings"]),
  isCustom: types.optional(types.boolean, false),
  building_id: types.number,
  project_id: types.maybeNull(types.number),
  folder_path: types.maybeNull(types.string),
  folder_depth: types.maybeNull(types.number),
  is_project_scoped: types.maybeNull(types.boolean),
});

/**
 * Document tags container model.
 */
export const DocumentTagsModel = types.model("DocumentTagsModel", {
  projects: types.map(ProjectTagModel),
  folders: types.map(FolderTagModel),
  customTags: types.array(types.string),
  isLoading: types.optional(types.boolean, false),
  error: types.maybeNull(types.string),
});

/**
 * Expanded cluster state model for timeline.
 * Stores dates as ISO strings for MST compatibility with computed Date views.
 */
export const ExpandedClusterModel = types
  .model("ExpandedClusterModel", {
    /** Start time as ISO string */
    startTimeISO: types.string,
    /** End time as ISO string */
    endTimeISO: types.string,
    /** Documents in this cluster (frozen since DocumentRowMapped is a complex type) */
    documents: types.array(types.frozen<DocumentRowMapped>()),
    /** Total count of documents in this time window */
    totalCount: types.number,
    /** Whether there are more documents to load */
    hasMore: types.boolean,
    /** Cursor timestamp as ISO string for pagination */
    cursorTimestampISO: types.maybeNull(types.string),
    /** Cursor ID for pagination */
    cursorId: types.maybeNull(types.number),
    /** Whether currently loading documents */
    isLoading: types.boolean,
    /**
     * Ids of the content-only matches the CLIENT spliced into this window on
     * top of the server's rows and count. A later page repeating one of them is
     * a double count to take back; a duplicate that is NOT in here was only ever
     * counted once. See `appendPageIntoWindow`.
     */
    splicedContentIds: types.optional(types.array(types.string), []),
    /**
     * True when paging this window gave up short of its end — a restart could
     * not re-page to the depth we held. `hasMore` is false because the cursor
     * is unusable, not because the window ran out, so the view offers a retry
     * and `loadMoreInCluster` re-reads the window instead of paging it.
     */
    truncated: types.optional(types.boolean, false),
  })
  .views((self) => ({
    /** Get start time as Date object */
    get startTime(): Date {
      return new Date(self.startTimeISO);
    },
    /** Get end time as Date object */
    get endTime(): Date {
      return new Date(self.endTimeISO);
    },
    /** Get cursor as object or null */
    get cursor(): { timestamp: Date; id: number } | null {
      if (self.cursorTimestampISO === null || self.cursorId === null) return null;
      return {
        timestamp: new Date(self.cursorTimestampISO),
        id: self.cursorId,
      };
    },
  }))
  .actions((self) => ({
    /** Set loading state */
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    /** Set documents after initial fetch */
    setDocuments(
      docs: DocumentRowMapped[],
      totalCount: number,
      hasMore: boolean,
      cursor: { timestamp: Date; id: number } | null,
      splicedContentIds: readonly string[],
      truncated = false
    ) {
      self.documents.replace(docs);
      self.splicedContentIds.replace([...splicedContentIds]);
      self.totalCount = totalCount;
      self.hasMore = hasMore;
      self.cursorTimestampISO = cursor?.timestamp.toISOString() ?? null;
      self.cursorId = cursor?.id ?? null;
      self.truncated = truncated;
      self.isLoading = false;
    },
    /** Replace just the document rows, leaving pagination state alone. Used to
     *  patch a row in place (e.g. after a rename) without a refetch. */
    setDocumentRows(docs: DocumentRowMapped[]) {
      self.documents.replace(docs);
    },
    /** Drop trashed rows and shrink the window count to match, so a bulk delete
     *  removes the cards without a refetch (see removeTrashedRowsFromTimeline). */
    removeDocuments(removedIds: ReadonlySet<string>) {
      const next = self.documents.filter((doc) => !removedIds.has(String(doc.id)));
      if (next.length === self.documents.length) return;
      // A row that has left the window is no longer a splice this cluster can
      // take a count back for — that already happened here.
      self.splicedContentIds.replace(self.splicedContentIds.filter((id) => !removedIds.has(id)));
      self.totalCount = Math.max(0, self.totalCount - (self.documents.length - next.length));
      self.documents.replace(next);
    },
    // A bare append action deliberately does not exist: page one holds the
    // window's content-only matches spliced in and sorted, so a later page has
    // to be deduped and re-sorted against what is already there. The store does
    // that in `appendPageIntoWindow` and writes the result through
    // `setDocuments`.
  }));
